import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import RegistrationForm from '../components/RegistrationForm';
import PricingCard from '../components/PricingCard';
import ServiceCard from '../components/ServiceCard';
import TestimonialCard from '../components/TestimonialCard';
import CountUp from '../components/CountUp';
import RegistrationModal from '../components/RegistrationModal';
import Slider from 'react-slick';
import { pricingPackages, services, testimonials, faqs, statistics, processSteps } from '../mock';
import { Shield, Clock, Users, CheckCircle2, ArrowRight } from 'lucide-react';

const HomePage = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleGetStarted = (pkg) => {
    // Open modal instead of scrolling
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  return (
    <div className="min-h-screen bg-slate-950">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 py-12 lg:py-20 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12">
            {/* Form First on Mobile, Content First on Desktop */}
            
            {/* Registration Form - Shows First on Mobile */}
            <div id="registration-form" className="flex justify-center lg:order-2">
              <RegistrationForm />
            </div>
            
            {/* Content - Shows Second on Mobile */}
            <div className="space-y-6 lg:order-1">
              <div className="inline-flex items-center space-x-2 bg-slate-800/50 backdrop-blur-sm px-4 py-2 rounded-full shadow-sm border border-blue-500/20">
                <Shield className="w-5 h-5 text-blue-400" />
                <span className="text-sm font-medium text-gray-200">Trusted Brand Protection</span>
              </div>
              
              <h1 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold text-white leading-tight">
                Protect Your Brand's <span className="text-blue-400">Identity & Future</span>
              </h1>
              
              <p className="text-base sm:text-lg lg:text-xl text-gray-300 leading-relaxed">
                Expert trademark registration services starting at just $45. We handle everything from comprehensive searches to USPTO filing, ensuring your brand receives complete legal protection. Fast processing, guaranteed results, and dedicated support every step of the way.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  onClick={handleGetStarted}
                  className="bg-gradient-to-r from-blue-700 to-blue-800 hover:from-amber-500 hover:to-amber-600 text-white px-6 sm:px-8 py-4 sm:py-6 text-base sm:text-lg transition-all duration-300 shadow-lg hover:shadow-xl w-full sm:w-auto"
                >
                  Get Started Now
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
                <Button variant="outline" className="chat js-chat border-blue-700 text-blue-700 hover:bg-amber-500 hover:text-white hover:border-amber-500 px-6 sm:px-8 py-4 sm:py-6 text-base sm:text-lg transition-all duration-300 w-full sm:w-auto">
                  Live Chat
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="bg-slate-900 py-12 lg:py-16 border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 lg:gap-8 text-center">
            <div className="space-y-2 bg-slate-800/30 p-6 rounded-lg">
              <div className="text-3xl sm:text-4xl font-bold bg-gradient-to-r from-blue-400 to-amber-500 bg-clip-text text-transparent">
                <CountUp end={100} suffix="%" />
              </div>
              <div className="text-sm sm:text-base text-gray-300">Client Satisfaction</div>
            </div>
            <div className="space-y-2 bg-slate-800/30 p-6 rounded-lg">
              <div className="text-3xl sm:text-4xl font-bold bg-gradient-to-r from-blue-400 to-amber-500 bg-clip-text text-transparent">
                <CountUp end={10} suffix="k+" />
              </div>
              <div className="text-sm sm:text-base text-gray-300">Trademarks Filed</div>
            </div>
            <div className="space-y-2 bg-slate-800/30 p-6 rounded-lg">
              <div className="text-3xl sm:text-4xl font-bold bg-gradient-to-r from-blue-400 to-amber-500 bg-clip-text text-transparent">
                <CountUp end={24} suffix="/7" />
              </div>
              <div className="text-sm sm:text-base text-gray-300">Support Available</div>
            </div>
          </div>
        </div>
      </section>

      {/* What We Do Section */}
      <section className="py-12 lg:py-20 bg-slate-800/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 lg:mb-12">
            <p className="text-blue-700 font-semibold mb-2 text-sm lg:text-base">WHAT WE DO</p>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-4">
              Streamlined Trademark Registration to Protect Your Brand
            </h2>
            <p className="text-sm sm:text-base text-gray-300 max-w-2xl mx-auto">
              At Filing Protection, we simplify the trademark registration process, offering businesses a fast, cost-effective way to secure their intellectual property.
            </p>
          </div>
          
          {/* Mobile: Slider, Desktop: Grid */}
          <div className="block lg:hidden">
            <Slider
              dots={true}
              infinite={true}
              speed={500}
              slidesToShow={1}
              slidesToScroll={1}
              autoplay={true}
              autoplaySpeed={3000}
            >
              <div className="px-2">
                <div className="bg-slate-900 p-6 rounded-xl shadow-sm border border-slate-700">
                  <div className="w-12 h-12 bg-blue-500/20 rounded-lg flex items-center justify-center mb-4">
                    <Shield className="w-6 h-6 text-blue-400" />
                  </div>
                  <h3 className="font-bold text-white mb-2">Trademark Search & Clearance</h3>
                  <p className="text-sm text-gray-300">Comprehensive database searches to ensure your mark is available</p>
                </div>
              </div>
              <div className="px-2">
                <div className="bg-slate-900 p-6 rounded-xl shadow-sm border border-slate-700">
                  <div className="w-12 h-12 bg-blue-500/20 rounded-lg flex items-center justify-center mb-4">
                    <CheckCircle2 className="w-6 h-6 text-blue-400" />
                  </div>
                  <h3 className="font-bold text-white mb-2">USPTO Trademark Filing</h3>
                  <p className="text-sm text-gray-300">Expert preparation and filing of your trademark application</p>
                </div>
              </div>
              <div className="px-2">
                <div className="bg-slate-900 p-6 rounded-xl shadow-sm border border-slate-700">
                  <div className="w-12 h-12 bg-blue-500/20 rounded-lg flex items-center justify-center mb-4">
                    <Users className="w-6 h-6 text-blue-400" />
                  </div>
                  <h3 className="font-bold text-white mb-2">Office Action Responses</h3>
                  <p className="text-sm text-gray-300">Professional handling of any USPTO objections or questions</p>
                </div>
              </div>
              <div className="px-2">
                <div className="bg-slate-900 p-6 rounded-xl shadow-sm border border-slate-700">
                  <div className="w-12 h-12 bg-blue-500/20 rounded-lg flex items-center justify-center mb-4">
                    <Clock className="w-6 h-6 text-blue-400" />
                  </div>
                  <h3 className="font-bold text-white mb-2">Trademark Renewals</h3>
                  <p className="text-sm text-gray-300">Timely reminders and assistance with trademark maintenance</p>
                </div>
              </div>
            </Slider>
          </div>

          {/* Desktop: Grid */}
          <div className="hidden lg:grid lg:grid-cols-2 xl:grid-cols-4 gap-6 lg:gap-8 mt-12">
            <div className="bg-slate-900 p-6 rounded-xl shadow-sm border border-slate-700 transition-all duration-300 hover:scale-105 hover:shadow-2xl hover:shadow-blue-500/20 hover:border-blue-400">
              <div className="w-12 h-12 bg-blue-500/20 rounded-lg flex items-center justify-center mb-4">
                <Shield className="w-6 h-6 text-blue-400" />
              </div>
              <h3 className="font-bold text-white mb-2">Trademark Search & Clearance</h3>
              <p className="text-sm text-gray-300">Comprehensive database searches to ensure your mark is available</p>
            </div>
            
            <div className="bg-slate-900 p-6 rounded-xl shadow-sm border border-slate-700 transition-all duration-300 hover:scale-105 hover:shadow-2xl hover:shadow-blue-500/20 hover:border-blue-400">
              <div className="w-12 h-12 bg-blue-500/20 rounded-lg flex items-center justify-center mb-4">
                <CheckCircle2 className="w-6 h-6 text-blue-400" />
              </div>
              <h3 className="font-bold text-white mb-2">USPTO Trademark Filing</h3>
              <p className="text-sm text-gray-300">Expert preparation and filing of your trademark application</p>
            </div>
            
            <div className="bg-slate-900 p-6 rounded-xl shadow-sm border border-slate-700 transition-all duration-300 hover:scale-105 hover:shadow-2xl hover:shadow-blue-500/20 hover:border-blue-400">
              <div className="w-12 h-12 bg-blue-500/20 rounded-lg flex items-center justify-center mb-4">
                <Users className="w-6 h-6 text-blue-400" />
              </div>
              <h3 className="font-bold text-white mb-2">Office Action Responses</h3>
              <p className="text-sm text-gray-300">Professional handling of any USPTO objections or questions</p>
            </div>
            
            <div className="bg-slate-900 p-6 rounded-xl shadow-sm border border-slate-700 transition-all duration-300 hover:scale-105 hover:shadow-2xl hover:shadow-blue-500/20 hover:border-blue-400">
              <div className="w-12 h-12 bg-blue-500/20 rounded-lg flex items-center justify-center mb-4">
                <Clock className="w-6 h-6 text-blue-400" />
              </div>
              <h3 className="font-bold text-white mb-2">Trademark Renewals</h3>
              <p className="text-sm text-gray-300">Timely reminders and assistance with trademark maintenance</p>
            </div>
          </div>
        </div>
      </section>

      {/* Process Steps Section */}
      <section className="py-12 lg:py-20 bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 lg:mb-12">
            <p className="text-blue-700 font-semibold mb-2 text-sm lg:text-base">SIMPLE PROCESS STEPS</p>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white">
              Three Steps To Protect Your Brand's Identity
            </h2>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 lg:gap-8">
            {processSteps.map((step) => (
              <div key={step.step} className="relative">
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6 lg:p-8 text-center transition-all duration-300 hover:shadow-2xl hover:shadow-blue-500/20 hover:border-blue-400">
                  <div className="w-14 h-14 lg:w-16 lg:h-16 bg-gradient-to-br from-blue-600 to-blue-700 text-white rounded-full flex items-center justify-center text-xl lg:text-2xl font-bold mx-auto mb-4 shadow-lg shadow-blue-500/50">
                    {step.step}
                  </div>
                  <h3 className="text-lg lg:text-xl font-bold text-white mb-3">{step.title}</h3>
                  <p className="text-sm lg:text-base text-gray-300">{step.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-12 lg:py-20 bg-slate-800/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 lg:mb-12">
            <p className="text-blue-400 font-semibold mb-2 text-sm lg:text-base">OUR SERVICES</p>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white">
              Our Professional Services
            </h2>
            <p className="text-sm sm:text-base text-gray-300 mt-4">
              Comprehensive intellectual property protection and business services tailored to your needs
            </p>
          </div>
          
          <Slider
            dots={true}
            infinite={true}
            speed={500}
            slidesToShow={3}
            slidesToScroll={1}
            autoplay={true}
            autoplaySpeed={3000}
            responsive={[
              {
                breakpoint: 1024,
                settings: {
                  slidesToShow: 2,
                  slidesToScroll: 1,
                }
              },
              {
                breakpoint: 640,
                settings: {
                  slidesToShow: 1,
                  slidesToScroll: 1
                }
              }
            ]}
          >
            {services.map((service) => (
              <div key={service.id} className="px-2 lg:px-4">
                <ServiceCard service={service} />
              </div>
            ))}
          </Slider>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-12 lg:py-20 bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 lg:mb-12">
            <p className="text-blue-400 font-semibold mb-2 text-sm lg:text-base">OUR PRICING</p>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white">
              Cost-Effective Trademark Solutions
            </h2>
            <p className="text-sm sm:text-base text-gray-300 mt-4">
              Choose the package that best fits your needs. All packages include expert support and guidance.
            </p>
          </div>
          
          {/* Grid Layout for All Devices */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8 mt-12">
            {pricingPackages.map((pkg) => (
              <div key={pkg.id}>
                <PricingCard package={pkg} onGetStarted={handleGetStarted} />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-12 lg:py-20 bg-gradient-to-br from-blue-900 via-blue-800 to-blue-700 text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-amber-500/10 to-transparent"></div>
        <div className="relative z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12">
            {/* Image First on Mobile */}
            <div className="order-1 lg:order-1">
              <img
                src="https://customer-assets.emergentagent.com/job_brand-registry-2/artifacts/tl0iug8k_why-choose-img.jpg"
                alt="Why Choose Us"
                className="rounded-xl shadow-2xl w-full"
              />
              <div className="grid grid-cols-2 gap-4 mt-6">
                <div className="bg-slate-900/10 backdrop-blur-sm rounded-lg p-4 text-center">
                  <div className="text-2xl lg:text-3xl font-bold">10K+</div>
                  <div className="text-xs lg:text-sm">Trademarks Filed</div>
                </div>
                <div className="bg-slate-900/10 backdrop-blur-sm rounded-lg p-4 text-center">
                  <div className="text-2xl lg:text-3xl font-bold">98%</div>
                  <div className="text-xs lg:text-sm">Success Rate</div>
                </div>
              </div>
            </div>
            
            {/* Text Second on Mobile */}
            <div className="order-2 lg:order-2">
              <p className="text-blue-100 font-semibold mb-2 text-sm lg:text-base">WHY CHOOSE US</p>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-4 lg:mb-6">
                Your Trusted Partner for Brand Protection
              </h2>
              <p className="text-sm sm:text-base text-blue-50 mb-6 lg:mb-8">
                We understand that protecting your brand is crucial to your business success. That's why we've built a reputation for excellence, reliability, and customer satisfaction.
              </p>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 lg:gap-6">
                <div className="bg-white/5 backdrop-blur-sm p-4 lg:p-6 rounded-xl border border-white/10 transition-all duration-300 hover:bg-white/10 hover:border-blue-400 hover:shadow-lg hover:shadow-blue-500/20">
                  <h3 className="font-bold mb-2 text-base lg:text-lg">Expert Protection</h3>
                  <p className="text-xs lg:text-sm text-blue-50">Our team of experienced IP attorneys ensures your brand receives the highest level of legal protection.</p>
                </div>
                <div className="bg-white/5 backdrop-blur-sm p-4 lg:p-6 rounded-xl border border-white/10 transition-all duration-300 hover:bg-white/10 hover:border-blue-400 hover:shadow-lg hover:shadow-blue-500/20">
                  <h3 className="font-bold mb-2 text-base lg:text-lg">24-Hour Processing</h3>
                  <p className="text-xs lg:text-sm text-blue-50">We process your trademark application swiftly within 24 hours, providing fast and efficient service.</p>
                </div>
                <div className="bg-white/5 backdrop-blur-sm p-4 lg:p-6 rounded-xl border border-white/10 transition-all duration-300 hover:bg-white/10 hover:border-blue-400 hover:shadow-lg hover:shadow-blue-500/20">
                  <h3 className="font-bold mb-2 text-base lg:text-lg">100% Satisfaction</h3>
                  <p className="text-xs lg:text-sm text-blue-50">We guarantee complete satisfaction with our services and stand behind our work with full confidence.</p>
                </div>
                <div className="bg-white/5 backdrop-blur-sm p-4 lg:p-6 rounded-xl border border-white/10 transition-all duration-300 hover:bg-white/10 hover:border-blue-400 hover:shadow-lg hover:shadow-blue-500/20">
                  <h3 className="font-bold mb-2 text-base lg:text-lg">Dedicated Support</h3>
                  <p className="text-xs lg:text-sm text-blue-50">24/7 customer support team ready to assist you at every step of your trademark journey.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-12 lg:py-20 bg-slate-800/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 lg:mb-12">
            <p className="text-blue-400 font-semibold mb-2 text-sm lg:text-base">TESTIMONIALS</p>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white">
              What Our Clients Say
            </h2>
            <p className="text-sm sm:text-base text-gray-300 mt-4">
              Hear from businesses we've helped protect and grow
            </p>
          </div>
          
          <Slider
            dots={true}
            infinite={true}
            speed={500}
            slidesToShow={3}
            slidesToScroll={1}
            autoplay={true}
            autoplaySpeed={4000}
            responsive={[
              {
                breakpoint: 1024,
                settings: {
                  slidesToShow: 2,
                  slidesToScroll: 1,
                }
              },
              {
                breakpoint: 640,
                settings: {
                  slidesToShow: 1,
                  slidesToScroll: 1
                }
              }
            ]}
          >
            {testimonials.map((testimonial) => (
              <div key={testimonial.id} className="px-4">
                <TestimonialCard testimonial={testimonial} />
              </div>
            ))}
          </Slider>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-12 lg:py-20 bg-gradient-to-br from-slate-800 via-slate-900 to-blue-950 relative overflow-hidden">
        <div className="absolute inset-0 bg-blue-500/5"></div>
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-8 lg:mb-12">
            <p className="text-blue-400 font-semibold mb-2 text-sm lg:text-base">FAQs</p>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white">
              Frequently Asked Questions
            </h2>
            <p className="text-sm sm:text-base text-gray-300 mt-4">
              Get answers to common questions about trademark registration and our services
            </p>
          </div>
          
          <Accordion type="single" collapsible className="w-full">
            {faqs.map((faq) => (
              <AccordionItem key={faq.id} value={`item-${faq.id}`}>
                <AccordionTrigger className="text-left font-semibold text-sm lg:text-base">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-gray-300 text-sm lg:text-base">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-12 lg:py-20 relative overflow-hidden text-white">
        {/* Background Image with Overlay */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1479142506502-19b3a3b7ff33?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwzfHxsZWdhbCUyMHNlcnZpY2VzfGVufDB8fHx8MTc2MzQwMTIxNXww&ixlib=rb-4.1.0&q=85)'
          }}
        ></div>
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900/95 via-blue-900/90 to-slate-900/95"></div>
        
        {/* Content */}
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-4">
            Don't Risk Your Brand's Future!
          </h2>
          <p className="text-base sm:text-lg lg:text-xl text-gray-200 mb-6 lg:mb-8">
            Unregistered trademarks can lead to costly legal battles. Protect your brand with Filing Protection' expert services—fast, reliable, and hassle-free. Secure your exclusive rights now before it's too late!
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Button 
              onClick={handleGetStarted}
              className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-amber-500 hover:to-amber-600 text-white px-6 sm:px-8 py-4 sm:py-6 text-base sm:text-lg shadow-xl transition-all duration-300 w-full sm:w-auto"
            >
              Start Your Application
            </Button>
          </div>
          <p className="text-xs sm:text-sm text-gray-300 mt-6">
            Special Offer: Trademark Registration starting at just $45 + USPTO fees
          </p>
        </div>
      </section>

      {/* Registration Modal */}
      <RegistrationModal isOpen={isModalOpen} onClose={handleCloseModal} />
    </div>
  );
};

export default HomePage;
